import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';

// Add script tag for Chart.js
const ChartJsScript = () => (
  <script 
    src="https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js"
    integrity="sha256-78S2gvxTrLIFSJBGtJ4HU2dGWS5/9dSho0cTXQYOFjY="
    crossOrigin="anonymous"
  ></script>
);

// Load the styles specific to HRT page
const hrtStyles = `
:root {
    --primary-color: #1d4ed8;
    --secondary-color: #3b82f6;
    --accent-color: #f97316;
    --accent-light: #ffedd5;
    --light-gray: #f8fafc;
    --medium-gray: #e2e8f0;
    --dark-gray: #64748b;
    --text-color: #1e293b;
    --text-light: #475569;
    --success-color: #10b981;
    --danger-color: #ef4444;
    --warning-color: #f59e0b;
    --info-color: #0ea5e9;
    --border-radius: 6px;
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --transition: all 0.3s ease;
}

.hrt-lecture * {
    box-sizing: border-box;
}

.hrt-lecture {
    line-height: 1.6;
    color: var(--text-color);
    background-color: #f9fafb;
}

.hrt-lecture h1, .hrt-lecture h2, .hrt-lecture h3 {
    font-family: 'Playfair Display', serif;
    line-height: 1.3;
}

.hrt-lecture h1 {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
}

.hrt-lecture h2 {
    font-size: 2rem;
    margin-bottom: 1.5rem;
    color: var(--primary-color);
    border-bottom: 2px solid var(--medium-gray);
    padding-bottom: 0.5rem;
}

.hrt-lecture h3 {
    font-size: 1.5rem;
    margin: 1.5rem 0 1rem;
    color: var(--text-color);
}

.hrt-lecture h4 {
    font-size: 1.25rem;
    margin: 1rem 0 0.5rem;
    color: var(--primary-color);
}

.hrt-lecture h5 {
    font-size: 1.1rem;
    margin: 0.75rem 0 0.25rem;
    color: var(--text-light);
}

.hrt-lecture p {
    margin-bottom: 1rem;
}

.hrt-lecture ul, .hrt-lecture ol {
    margin: 0 0 1.5rem 1.25rem;
}

.hrt-lecture li {
    margin-bottom: 0.5rem;
}

.hrt-lecture .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.5rem;
}

/* Header and Navigation */
.hrt-lecture header {
    background: linear-gradient(to right, #1e40af, #3b82f6);
    color: white;
    padding: 2rem 0;
    position: relative;
}

.hrt-lecture .header-content {
    text-align: center;
    padding: 2rem 0;
}

.hrt-lecture .subtitle {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
}

.hrt-lecture #main-nav {
    background-color: rgba(0, 0, 0, 0.1);
    padding: 0.75rem 0;
    position: sticky;
    top: 0;
    z-index: 100;
}

.hrt-lecture #main-nav ul {
    display: flex;
    justify-content: center;
    list-style: none;
    margin: 0;
    padding: 0;
    flex-wrap: wrap;
}

.hrt-lecture #main-nav li {
    margin: 0 0.75rem;
    margin-bottom: 0;
}

.hrt-lecture #main-nav a {
    color: white;
    text-decoration: none;
    font-weight: 500;
    padding: 0.5rem 0.75rem;
    border-radius: var(--border-radius);
    transition: var(--transition);
}

.hrt-lecture #main-nav a:hover {
    background-color: rgba(255, 255, 255, 0.15);
}

/* Main Content Sections */
.hrt-lecture .section {
    padding: 3rem 0;
}

.hrt-lecture .section:nth-child(even) {
    background-color: #f1f5f9;
}

.hrt-lecture .subsection {
    margin-bottom: 2.5rem;
}

/* Grid Layouts */
.hrt-lecture .grid-2-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
}

.hrt-lecture .grid-3-col {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
}

.hrt-lecture .grid-4-col {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
}

/* Cards and Containers */
.hrt-lecture .key-points-card {
    background-color: #f0f9ff;
    border-left: 4px solid var(--secondary-color);
    padding: 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
}

.hrt-lecture .optimization-card {
    background-color: white;
    padding: 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
}

.hrt-lecture .optimization-card h4 {
    color: var(--primary-color);
    margin-top: 0;
}

.hrt-lecture .peptide-card, .hrt-lecture .protocol-card, .hrt-lecture .fertility-card, .hrt-lecture .special-card {
    background-color: white;
    padding: 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    height: 100%;
}

.hrt-lecture .contraindication-card {
    padding: 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
}

.hrt-lecture .contraindication-card.absolute {
    background-color: #fee2e2;
    border-left: 4px solid var(--danger-color);
}

.hrt-lecture .contraindication-card.relative {
    background-color: #fef3c7;
    border-left: 4px solid var(--warning-color);
}

.hrt-lecture .mechanism-card {
    background-color: white;
    padding: 1.25rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow-sm);
    border-top: 3px solid var(--primary-color);
}

.hrt-lecture .lifestyle-card {
    background-color: white;
    padding: 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    text-align: center;
}

.hrt-lecture .image-container {
    margin: 2rem 0;
    text-align: center;
}

.hrt-lecture .diagram {
    max-width: 100%;
    height: auto;
    margin: 0 auto;
    background-color: white;
    padding: 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
}

.hrt-lecture .caption {
    font-size: 0.9rem;
    color: var(--text-light);
    margin-top: 0.75rem;
}

/* Tabs */
.hrt-lecture .tabs {
    margin: 1.5rem 0;
}

.hrt-lecture .tab-headers {
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid var(--medium-gray);
}

.hrt-lecture .tab-btn {
    padding: 0.75rem 1.25rem;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    font-weight: 500;
    color: var(--text-light);
    transition: var(--transition);
}

.hrt-lecture .tab-btn:hover {
    color: var(--primary-color);
}

.hrt-lecture .tab-btn.active {
    color: var(--primary-color);
    border-bottom-color: var(--primary-color);
}

.hrt-lecture .tab-content {
    display: none;
    padding: 1.5rem 0;
}

.hrt-lecture .tab-content.active {
    display: block;
}

.hrt-lecture .test-list {
    list-style-type: none;
    margin: 0;
}

.hrt-lecture .test-list li {
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--medium-gray);
}

.hrt-lecture .test-list li:last-child {
    border-bottom: none;
}

/* Application Routes */
.hrt-lecture .application-routes {
    margin-top: 1rem;
}

.hrt-lecture .route-card {
    display: flex;
    align-items: center;
    background-color: white;
    padding: 1rem;
    border-radius: var(--border-radius);
    margin-bottom: 1rem;
    box-shadow: var(--shadow-sm);
}

.hrt-lecture .route-icon {
    width: 50px;
    height: 50px;
    min-width: 50px;
    border-radius: 50%;
    margin-right: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--light-gray);
}

.hrt-lecture .im-icon {
    background-color: #dbeafe;
    position: relative;
}

.hrt-lecture .im-icon::after {
    content: "";
    width: 20px;
    height: 6px;
    background-color: #3b82f6;
    position: absolute;
    border-radius: 3px;
}

.hrt-lecture .transdermal-icon {
    background-color: #dcfce7;
    position: relative;
}

.hrt-lecture .transdermal-icon::after {
    content: "";
    width: 20px;
    height: 20px;
    border: 3px solid #10b981;
    border-radius: 50%;
    position: absolute;
}

.hrt-lecture .pellet-icon {
    background-color: #fef3c7;
    position: relative;
}

.hrt-lecture .pellet-icon::after {
    content: "";
    width: 12px;
    height: 12px;
    background-color: #f59e0b;
    border-radius: 50%;
    position: absolute;
}

.hrt-lecture .buccal-icon {
    background-color: #ede9fe;
    position: relative;
}

.hrt-lecture .buccal-icon::after {
    content: "";
    width: 20px;
    height: 15px;
    border: 3px solid #8b5cf6;
    border-radius: 30px 30px 0 0;
    position: absolute;
    border-bottom: none;
}

.hrt-lecture .route-details h5 {
    margin-top: 0;
    margin-bottom: 0.25rem;
}

.hrt-lecture .route-details p {
    margin-bottom: 0;
    font-size: 0.9rem;
}

/* Accordion */
.hrt-lecture .accordion {
    margin: 1.5rem 0;
}

.hrt-lecture .accordion-item {
    border: 1px solid var(--medium-gray);
    border-radius: var(--border-radius);
    margin-bottom: 0.75rem;
    overflow: hidden;
}

.hrt-lecture .accordion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    background-color: white;
    cursor: pointer;
    transition: var(--transition);
}

.hrt-lecture .accordion-header:hover {
    background-color: var(--light-gray);
}

.hrt-lecture .accordion-header h4 {
    margin: 0;
}

.hrt-lecture .accordion-icon {
    font-size: 1.5rem;
    color: var(--text-light);
}

.hrt-lecture .accordion-content {
    padding: 0 1.5rem;
    max-height: 0;
    overflow: hidden;
    transition: var(--transition);
}

.hrt-lecture .accordion-item.active .accordion-content {
    padding: 1rem 1.5rem;
    max-height: 1000px;
}

.hrt-lecture .accordion-item.active .accordion-icon {
    transform: rotate(45deg);
}

/* Timeline */
.hrt-lecture .timeline {
    margin: 2rem 0;
    position: relative;
}

.hrt-lecture .timeline::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 48px;
    width: 2px;
    background-color: var(--medium-gray);
}

.hrt-lecture .timeline-item {
    position: relative;
    padding-left: 100px;
    margin-bottom: 1.5rem;
}

.hrt-lecture .timeline-marker {
    position: absolute;
    left: 0;
    width: 96px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--primary-color);
    color: white;
    font-weight: 500;
    border-radius: var(--border-radius);
}

.hrt-lecture .timeline-content {
    background-color: white;
    padding: 1rem 1.5rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow-sm);
}

/* Outcomes */
.hrt-lecture .outcomes-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin: 1.5rem 0;
}

.hrt-lecture .outcome-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.hrt-lecture .outcome-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--light-gray);
}

.hrt-lecture .energy-icon {
    background-color: #fbcfe8;
    position: relative;
}

.hrt-lecture .energy-icon::after {
    content: "⚡";
    font-size: 1.5rem;
}

.hrt-lecture .libido-icon {
    background-color: #dbeafe;
    position: relative;
}

.hrt-lecture .libido-icon::after {
    content: "♥";
    font-size: 1.5rem;
    color: #3b82f6;
}

.hrt-lecture .muscle-icon {
    background-color: #bae6fd;
    position: relative;
}

.hrt-lecture .muscle-icon::after {
    content: "💪";
    font-size: 1.5rem;
}

.hrt-lecture .fat-icon {
    background-color: #fef3c7;
    position: relative;
}

.hrt-lecture .fat-icon::after {
    content: "↓";
    font-size: 1.5rem;
    color: #d97706;
}

.hrt-lecture .sleep-icon {
    background-color: #d8b4fe;
    position: relative;
}

.hrt-lecture .sleep-icon::after {
    content: "😴";
    font-size: 1.5rem;
}

.hrt-lecture .mental-icon {
    background-color: #a7f3d0;
    position: relative;
}

.hrt-lecture .mental-icon::after {
    content: "🧠";
    font-size: 1.5rem;
}

/* Tables */
.hrt-lecture .evidence-table, .hrt-lecture .dosing-table {
    border-collapse: collapse;
    width: 100%;
    margin: 1.5rem 0;
}

.hrt-lecture .table-row {
    display: flex;
    border-bottom: 1px solid var(--medium-gray);
}

.hrt-lecture .table-row.header {
    background-color: var(--primary-color);
    color: white;
    font-weight: 500;
}

.hrt-lecture .table-row:last-child {
    border-bottom: none;
}

.hrt-lecture .cell {
    padding: 0.75rem 1rem;
    flex: 1;
}

.hrt-lecture .table-row.header .cell {
    padding: 1rem;
}

/* Charts */
.hrt-lecture .chart-container {
    height: 250px;
    width: 100%;
    background-color: white;
    padding: 1rem;
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
}

/* Lifestyle Icons */
.hrt-lecture .lifestyle-icon {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    margin: 0 auto 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
}

.hrt-lecture .diet-icon {
    background-color: #dcfce7;
    position: relative;
}

.hrt-lecture .diet-icon::after {
    content: "🥗";
    font-size: 1.75rem;
}

.hrt-lecture .exercise-icon {
    background-color: #dbeafe;
    position: relative;
}

.hrt-lecture .exercise-icon::after {
    content: "🏋️";
    font-size: 1.75rem;
}

.hrt-lecture .stress-icon {
    background-color: #f5f3ff;
    position: relative;
}

.hrt-lecture .stress-icon::after {
    content: "🧘";
    font-size: 1.75rem;
}

.hrt-lecture .sleep-icon {
    background-color: #ede9fe;
    position: relative;
}

.hrt-lecture .sleep-icon::after {
    content: "😴";
    font-size: 1.75rem;
}

/* Conclusion */
.hrt-lecture .citation-note {
    background-color: var(--light-gray);
    padding: 1.5rem;
    border-radius: var(--border-radius);
    border-left: 4px solid var(--text-light);
    margin-top: 2rem;
}

/* Mechanism Grid */
.hrt-lecture .mechanism-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin: 1.5rem 0;
}

/* Peptide Grid */
.hrt-lecture .peptide-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin: 1.5rem 0;
}

/* Responsive */
@media (max-width: 1024px) {
    .hrt-lecture .grid-4-col {
        grid-template-columns: repeat(2, 1fr);
    }
    
    .hrt-lecture .mechanism-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (max-width: 768px) {
    .hrt-lecture .grid-2-col, .hrt-lecture .grid-3-col, .hrt-lecture .peptide-grid {
        grid-template-columns: 1fr;
    }
    
    .hrt-lecture .outcomes-grid, .hrt-lecture .mechanism-grid {
        grid-template-columns: repeat(2, 1fr);
    }
    
    .hrt-lecture h1 {
        font-size: 2rem;
    }
    
    .hrt-lecture h2 {
        font-size: 1.75rem;
    }
}

@media (max-width: 500px) {
    .hrt-lecture .grid-4-col, .hrt-lecture .outcomes-grid, .hrt-lecture .mechanism-grid {
        grid-template-columns: 1fr;
    }
    
    .hrt-lecture .timeline-item {
        padding-left: 60px;
    }
    
    .hrt-lecture .timeline-marker {
        width: 50px;
        height: 28px;
        font-size: 0.85rem;
    }
    
    .hrt-lecture .timeline::before {
        left: 25px;
    }
}
`;

// Create a component that will handle the chart.js and SVG diagrams
const HRTScripts = () => {
  useEffect(() => {
    // Function to create Chart.js chart
    function createEstradiolRatioChart() {
      const ctx = document.getElementById('estradiolChart') as HTMLCanvasElement | null;
      if (!ctx) return;
      
      // For safety, we'll check if Chart.js is loaded globally
      // In a real implementation, you would import Chart.js properly
      if (typeof window !== 'undefined' && window.hasOwnProperty('Chart')) {
        // @ts-ignore - Using Chart.js from CDN
        new window.Chart(ctx, {
          type: 'bar',
          data: {
            labels: ['Too Low (<2%)', 'Optimal (3-5%)', 'Too High (>5%)'],
            datasets: [{
              label: 'Estradiol:Testosterone Ratio',
              data: [1.5, 4, 6.5],
              backgroundColor: [
                'rgba(239, 68, 68, 0.6)',  // red
                'rgba(16, 185, 129, 0.6)', // green
                'rgba(239, 68, 68, 0.6)'   // red
              ],
              borderColor: [
                'rgb(239, 68, 68)',  // red
                'rgb(16, 185, 129)', // green
                'rgb(239, 68, 68)'   // red
              ],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'E2:T Ratio (%)'
                },
                max: 8
              }
            },
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: function(context: any) {
                    let label = '';
                    
                    if (context.dataIndex === 0) {
                      label = 'Insufficient estrogen: Potential for bone loss, cognitive issues';
                    } else if (context.dataIndex === 1) {
                      label = 'Balanced: Optimal metabolic and physiological function';
                    } else if (context.dataIndex === 2) {
                      label = 'Excessive estrogen: Risk of gynecomastia, mood changes, fluid retention';
                    }
                    
                    return label;
                  }
                }
              }
            }
          }
        });
      } else {
        console.log('Chart.js not loaded');
      }
    }

    // Function to create HPTA diagram
    function createHPTADiagram() {
      const diagram = document.getElementById('hpta-diagram');
      if (!diagram) return;
      
      const svg = `
      <svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg">
          <!-- Background -->
          <rect x="0" y="0" width="800" height="500" fill="#f8fafc" rx="5" />
          
          <!-- Hypothalamus -->
          <ellipse cx="400" cy="100" rx="100" ry="60" fill="#dbeafe" stroke="#3b82f6" stroke-width="2" />
          <text x="400" y="100" text-anchor="middle" font-size="18" font-weight="bold" fill="#1e40af">Hypothalamus</text>
          <text x="400" y="125" text-anchor="middle" font-size="14" fill="#1e40af">GnRH</text>
          
          <!-- Arrow down to Pituitary -->
          <path d="M400,160 L400,190" stroke="#64748b" stroke-width="3" marker-end="url(#arrowhead)" />
          <text x="420" y="180" font-size="14" fill="#64748b">GnRH pulses</text>
          
          <!-- Pituitary -->
          <ellipse cx="400" cy="240" rx="80" ry="50" fill="#f0fdf4" stroke="#10b981" stroke-width="2" />
          <text x="400" y="235" text-anchor="middle" font-size="18" font-weight="bold" fill="#047857">Pituitary</text>
          <text x="400" y="260" text-anchor="middle" font-size="14" fill="#047857">LH / FSH</text>
          
          <!-- Arrow down to Testes -->
          <path d="M400,290 L400,330" stroke="#64748b" stroke-width="3" marker-end="url(#arrowhead)" />
          <text x="420" y="320" font-size="14" fill="#64748b">LH stimulates</text>
          
          <!-- Testes -->
          <ellipse cx="400" cy="380" rx="90" ry="50" fill="#eff6ff" stroke="#3b82f6" stroke-width="2" />
          <text x="400" y="375" text-anchor="middle" font-size="18" font-weight="bold" fill="#1d4ed8">Testes</text>
          <text x="400" y="400" text-anchor="middle" font-size="14" fill="#1d4ed8">Testosterone / Inhibin</text>
          
          <!-- Feedback Arrows -->
          <path d="M310,380 C200,380 200,100 300,100" stroke="#ef4444" stroke-width="3" stroke-dasharray="5,5" marker-end="url(#negative-arrowhead)" />
          <text x="220" y="250" font-size="14" fill="#b91c1c" transform="rotate(-90, 220, 250)">Negative Feedback</text>
          
          <path d="M490,380 C600,380 600,240 480,240" stroke="#ef4444" stroke-width="3" stroke-dasharray="5,5" marker-end="url(#negative-arrowhead)" />
          <text x="570" y="320" font-size="14" fill="#b91c1c" transform="rotate(90, 570, 320)">Negative Feedback</text>
          
          <!-- Target Tissues -->
          <rect x="600" y="380" width="120" height="80" rx="10" fill="#fdf2f8" stroke="#ec4899" stroke-width="2" />
          <text x="660" y="410" text-anchor="middle" font-size="16" font-weight="bold" fill="#be185d">Target Tissues</text>
          <text x="660" y="435" text-anchor="middle" font-size="14" fill="#be185d">Multiple Effects</text>
          
          <!-- Aromatization -->
          <rect x="100" y="380" width="120" height="80" rx="10" fill="#fdf4ff" stroke="#c026d3" stroke-width="2" />
          <text x="160" y="410" text-anchor="middle" font-size="16" font-weight="bold" fill="#a21caf">Aromatization</text>
          <text x="160" y="435" text-anchor="middle" font-size="14" fill="#a21caf">Estradiol (E2)</text>
          
          <!-- Arrow to target tissues -->
          <path d="M490,380 L600,410" stroke="#64748b" stroke-width="2" marker-end="url(#arrowhead)" />
          
          <!-- Arrow to aromatization -->
          <path d="M310,380 L220,410" stroke="#64748b" stroke-width="2" marker-end="url(#arrowhead)" />
          
          <!-- Arrowhead definitions -->
          <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
              </marker>
              <marker id="negative-arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#ef4444" />
              </marker>
          </defs>
      </svg>
      `;
      
      diagram.innerHTML = svg;
    }

    // Function to create testosterone metabolism diagram
    function createTestosteroneMetabolismDiagram() {
      const diagram = document.getElementById('testosterone-metabolism');
      if (!diagram) return;
      
      const svg = `
      <svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg">
          <!-- Background -->
          <rect x="0" y="0" width="800" height="500" fill="#f8fafc" rx="5" />
          
          <!-- Testosterone -->
          <rect x="300" y="50" width="200" height="80" rx="10" fill="#dbeafe" stroke="#3b82f6" stroke-width="3" />
          <text x="400" y="90" text-anchor="middle" font-size="22" font-weight="bold" fill="#1e40af">Testosterone</text>
          
          <!-- Conversion Pathways -->
          <!-- DHT Pathway -->
          <path d="M300,130 L200,200" stroke="#64748b" stroke-width="3" marker-end="url(#arrowhead)" />
          <text x="230" y="170" font-size="14" fill="#64748b">5α-reductase</text>
          
          <rect x="100" y="200" width="180" height="70" rx="10" fill="#f0fdf4" stroke="#10b981" stroke-width="3" />
          <text x="190" y="235" text-anchor="middle" font-size="18" font-weight="bold" fill="#047857">Dihydrotestosterone</text>
          <text x="190" y="255" text-anchor="middle" font-size="14" fill="#047857">(DHT)</text>
          
          <!-- Estradiol Pathway -->
          <path d="M500,130 L600,200" stroke="#64748b" stroke-width="3" marker-end="url(#arrowhead)" />
          <text x="570" y="170" font-size="14" fill="#64748b">Aromatase</text>
          
          <rect x="520" y="200" width="180" height="70" rx="10" fill="#fdf2f8" stroke="#ec4899" stroke-width="3" />
          <text x="610" y="235" text-anchor="middle" font-size="18" font-weight="bold" fill="#be185d">Estradiol</text>
          <text x="610" y="255" text-anchor="middle" font-size="14" fill="#be185d">(E2)</text>
          
          <!-- Direct Testosterone Effects -->
          <path d="M400,130 L400,200" stroke="#64748b" stroke-width="3" marker-end="url(#arrowhead)" />
          <text x="420" y="170" font-size="14" fill="#64748b">Direct Effects</text>
          
          <rect x="310" y="200" width="180" height="70" rx="10" fill="#eff6ff" stroke="#3b82f6" stroke-width="3" />
          <text x="400" y="235" text-anchor="middle" font-size="18" font-weight="bold" fill="#1d4ed8">Androgen Receptor</text>
          <text x="400" y="255" text-anchor="middle" font-size="14" fill="#1d4ed8">Binding</text>
          
          <!-- Effects of DHT -->
          <path d="M190,270 L190,330" stroke="#64748b" stroke-width="2" marker-end="url(#arrowhead)" />
          
          <rect x="100" y="330" width="180" height="120" rx="5" fill="#ecfdf5" stroke="#10b981" stroke-width="2" />
          <text x="190" y="350" text-anchor="middle" font-size="16" font-weight="bold" fill="#047857">DHT Effects</text>
          <text x="190" y="375" text-anchor="middle" font-size="14" fill="#047857">• Prostate growth</text>
          <text x="190" y="395" text-anchor="middle" font-size="14" fill="#047857">• Hair follicle miniaturization</text>
          <text x="190" y="415" text-anchor="middle" font-size="14" fill="#047857">• Masculine facial features</text>
          <text x="190" y="435" text-anchor="middle" font-size="14" fill="#047857">• Stronger AR binding</text>
          
          <!-- Effects of Testosterone -->
          <path d="M400,270 L400,330" stroke="#64748b" stroke-width="2" marker-end="url(#arrowhead)" />
          
          <rect x="310" y="330" width="180" height="120" rx="5" fill="#dbeafe" stroke="#3b82f6" stroke-width="2" />
          <text x="400" y="350" text-anchor="middle" font-size="16" font-weight="bold" fill="#1e40af">Testosterone Effects</text>
          <text x="400" y="375" text-anchor="middle" font-size="14" fill="#1e40af">• Muscle anabolism</text>
          <text x="400" y="395" text-anchor="middle" font-size="14" fill="#1e40af">• Erythropoiesis</text>
          <text x="400" y="415" text-anchor="middle" font-size="14" fill="#1e40af">• Bone density</text>
          <text x="400" y="435" text-anchor="middle" font-size="14" fill="#1e40af">• Libido and mood</text>
          
          <!-- Effects of Estradiol -->
          <path d="M610,270 L610,330" stroke="#64748b" stroke-width="2" marker-end="url(#arrowhead)" />
          
          <rect x="520" y="330" width="180" height="120" rx="5" fill="#fdf2f8" stroke="#ec4899" stroke-width="2" />
          <text x="610" y="350" text-anchor="middle" font-size="16" font-weight="bold" fill="#be185d">Estradiol Effects</text>
          <text x="610" y="375" text-anchor="middle" font-size="14" fill="#be185d">• Fluid retention</text>
          <text x="610" y="395" text-anchor="middle" font-size="14" fill="#be185d">• Fat distribution</text>
          <text x="610" y="415" text-anchor="middle" font-size="14" fill="#be185d">• Gynecomastia risk</text>
          <text x="610" y="435" text-anchor="middle" font-size="14" fill="#be185d">• Bone health</text>
          
          <!-- Arrowhead definitions -->
          <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
              </marker>
          </defs>
      </svg>
      `;
      
      diagram.innerHTML = svg;
    }

    // Tab functionality
    const tabButtons = document.querySelectorAll('.tab-btn');
    
    if (tabButtons) {
      tabButtons.forEach(button => {
        button.addEventListener('click', () => {
          // Remove active class from all tabs
          document.querySelectorAll('.tab-btn').forEach(btn => {
            btn.classList.remove('active');
          });
          
          document.querySelectorAll('.tab-content').forEach(content => {
            content.classList.remove('active');
          });
          
          // Add active class to clicked tab
          button.classList.add('active');
          const tabId = button.getAttribute('data-tab');
          if (tabId) {
            const tabContent = document.getElementById(tabId);
            if (tabContent) {
              tabContent.classList.add('active');
            }
          }
        });
      });
    }
    
    // Accordion functionality
    const accordionHeaders = document.querySelectorAll('.accordion-header');
    
    if (accordionHeaders) {
      accordionHeaders.forEach(header => {
        header.addEventListener('click', () => {
          const accordionItem = header.parentElement;
          if (accordionItem) {
            const isActive = accordionItem.classList.contains('active');
            
            // Close all accordion items
            document.querySelectorAll('.accordion-item').forEach(item => {
              item.classList.remove('active');
            });
            
            // If clicked item wasn't active, open it
            if (!isActive) {
              accordionItem.classList.add('active');
            }
          }
        });
      });
    }
    
    // Create diagrams and charts
    createHPTADiagram();
    createTestosteroneMetabolismDiagram();
    createEstradiolRatioChart();
  }, []);

  return null;
};

const SampleMaleHRT = () => {
  return (
    <div className="min-h-screen">
      {/* Add the page-specific styles */}
      <style dangerouslySetInnerHTML={{ __html: hrtStyles }} />
      
      {/* Add the Chart.js script */}
      <ChartJsScript />
      
      {/* Link back to home */}
      <div className="bg-background p-4 border-b">
        <div className="container mx-auto">
          <a href="/" className="text-primary hover:underline flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Home
          </a>
        </div>
      </div>
      
      {/* Main content */}
      <div className="hrt-lecture">
        <header>
          <div className="header-content">
            <h1>Comprehensive Clinical Protocol for Hormone Replacement Therapy in Men</h1>
            <p className="subtitle">A Mechanistic & Practical Implementation Guide</p>
          </div>
          <nav id="main-nav">
            <ul>
              <li><a href="#introduction">Introduction</a></li>
              <li><a href="#assessment">Initial Assessment</a></li>
              <li><a href="#hrt-protocol">HRT Protocol</a></li>
              <li><a href="#monitoring">Monitoring</a></li>
              <li><a href="#special-considerations">Special Considerations</a></li>
              <li><a href="#scientific-rationale">Scientific Rationale</a></li>
            </ul>
          </nav>
        </header>

        <main>
          <section id="introduction" className="section">
            <div className="container">
              <h2>Introduction</h2>
              <div className="grid-2-col">
                <div>
                  <p>Hormone Replacement Therapy (HRT) in men, often referred to as Testosterone Replacement Therapy (TRT), is a multifaceted intervention designed to address hypogonadism and age-related hormonal decline. This comprehensive protocol synthesizes mechanistic insights, practical clinical strategies, and detailed monitoring approaches to optimize endocrine balance, cellular efficiency, and overall health in men.</p>
                  <p>It integrates the use of bioidentical hormones, adjunctive agents, peptide therapeutics, and lifestyle interventions to create an individualized approach to male hormonal health.</p>
                </div>
                <div className="key-points-card">
                  <h3>Key Principles</h3>
                  <ul>
                    <li>Physiologic replacement, not supraphysiologic enhancement</li>
                    <li>Multi-hormone approach beyond testosterone alone</li>
                    <li>Integration with peptides and lifestyle optimization</li>
                    <li>Regular monitoring and personalized adjustment</li>
                    <li>Root-cause focused assessment</li>
                  </ul>
                </div>
              </div>
              <div className="image-container">
                <div id="hpta-diagram" className="diagram">
                  {/* Diagram will be inserted by JavaScript */}
                </div>
                <p className="caption">Figure 1: Hypothalamic-Pituitary-Testicular Axis (HPTA) Regulation</p>
              </div>
            </div>
          </section>

          <section id="assessment" className="section">
            <div className="container">
              <h2>I. Initial Assessment and Optimization</h2>
              
              <div className="subsection">
                <h3>1. Baseline Evaluation</h3>
                <p>A thorough baseline assessment is critical to determine candidacy for HRT, identify underlying causes of hormonal imbalance, and establish a reference for monitoring therapy.</p>
                
                <div className="tabs">
                  <div className="tab-headers">
                    <button className="tab-btn active" data-tab="hormonal">Hormonal Panel</button>
                    <button className="tab-btn" data-tab="metabolic">Metabolic Panel</button>
                    <button className="tab-btn" data-tab="inflammation">Inflammatory Markers</button>
                    <button className="tab-btn" data-tab="additional">Additional Assessments</button>
                    <button className="tab-btn" data-tab="physical">Physical Examination</button>
                  </div>
                  
                  <div id="hormonal" className="tab-content active">
                    <ul className="test-list">
                      <li><strong>Total and Free Testosterone:</strong> To assess androgen status</li>
                      <li><strong>Estradiol (E2):</strong> Evaluates aromatization and estrogenic activity</li>
                      <li><strong>DHEA-S:</strong> Marker of adrenal androgen production</li>
                      <li><strong>Cortisol (AM and PM):</strong> Assesses adrenal function and diurnal rhythm</li>
                      <li><strong>Luteinizing Hormone (LH) and Follicle-Stimulating Hormone (FSH):</strong> Distinguish primary from secondary hypogonadism</li>
                      <li><strong>IGF-1:</strong> Reflects growth hormone (GH) axis activity</li>
                      <li><strong>Pregnenolone:</strong> Precursor to steroid hormones</li>
                      <li><strong>Prolactin:</strong> Rules out prolactinoma</li>
                      <li><strong>Sex Hormone-Binding Globulin (SHBG):</strong> Influences free testosterone calculation</li>
                      <li><strong>Thyroid Panel (TSH, Free T3, Free T4):</strong> Thyroid dysfunction can mimic or exacerbate hypogonadism</li>
                    </ul>
                  </div>
                  
                  <div id="metabolic" className="tab-content">
                    <ul className="test-list">
                      <li><strong>Fasting Glucose, Insulin, HbA1c:</strong> Evaluate glycemic control</li>
                      <li><strong>Lipid Profile:</strong> Total cholesterol, HDL, LDL, triglycerides</li>
                      <li><strong>Comprehensive Metabolic Panel (CMP):</strong> Liver and kidney function</li>
                    </ul>
                  </div>
                  
                  <div id="inflammation" className="tab-content">
                    <ul className="test-list">
                      <li><strong>IL-6, TNF-α:</strong> Pro-inflammatory cytokines</li>
                      <li><strong>CRP, hsCRP:</strong> Markers of systemic inflammation</li>
                      <li><strong>Homocysteine:</strong> Cardiovascular risk marker</li>
                    </ul>
                  </div>
                  
                  <div id="additional" className="tab-content">
                    <ul className="test-list">
                      <li><strong>Sleep and Circadian Rhythm:</strong> Subjective sleep quality, screen for sleep apnea</li>
                      <li><strong>Body Composition:</strong> DEXA scan for visceral fat and lean mass</li>
                      <li><strong>Organic Acids Test (optional):</strong> Neurotransmitter metabolism, mitochondrial function</li>
                    </ul>
                  </div>
                  
                  <div id="physical" className="tab-content">
                    <ul className="test-list">
                      <li><strong>Vital Signs:</strong> Blood pressure, heart rate</li>
                      <li><strong>Anthropometrics:</strong> BMI, waist circumference</li>
                      <li><strong>Testicular and Prostate Exam:</strong> Size, consistency, digital rectal exam (for men {'>'}40)</li>
                      <li><strong>Gynecomastia and Hair Pattern:</strong> Signs of hormonal imbalance</li>
                      <li><strong>Symptom Assessment:</strong> Validated questionnaires (ADAM, EMS) to document baseline symptoms</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="subsection">
                <h3>2. Pre-HRT Optimization</h3>
                <p>Before initiating HRT, address modifiable factors to optimize therapy outcomes and minimize risks.</p>
                
                <div className="grid-3-col">
                  <div className="optimization-card">
                    <h4>Nutritional Intervention</h4>
                    <p><strong>Fasting-Mimicking Diet (e.g., Prolon):</strong> Enhances autophagy, reduces senescent cells, improves metabolic flexibility.</p>
                  </div>
                  
                  <div className="optimization-card">
                    <h4>Gut Health</h4>
                    <p><strong>Oral BPC-157 and L-Glutamine:</strong> Support epithelial repair and gut-brain axis function.</p>
                  </div>
                  
                  <div className="optimization-card">
                    <h4>Peptide Priming</h4>
                    <p><strong>Tesamorelin and Ipamorelin (4 weeks):</strong> Reduce visceral fat, stimulate endogenous GH.</p>
                    <p><strong>CJC-1295 and Ipamorelin (subsequent 4 weeks):</strong> Mimic physiological GH pulsatility.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="hrt-protocol" className="section">
            <div className="container">
              <h2>II. Hormone Replacement Therapy Protocol</h2>
              
              <div className="subsection">
                <h3>1. Testosterone Replacement</h3>
                
                <div className="grid-2-col">
                  <div>
                    <h4>Indications</h4>
                    <p>Clinical symptoms of hypogonadism with laboratory confirmation (total testosterone &lt;300–350 ng/dL, free testosterone &lt;16 ng/dL).</p>
                    
                    <h4>Dose Titration</h4>
                    <ul>
                      <li>Adjust to achieve mid-physiologic free testosterone (target: 400–700 ng/dL total, 20–25 ng/dL free)</li>
                      <li>Avoid supraphysiologic levels to prevent premature senescence and adverse effects</li>
                    </ul>
                    
                    <h4>Monitoring</h4>
                    <p>Reassess testosterone, estradiol, and hematocrit every 8–12 weeks initially, then every 3–6 months.</p>
                  </div>
                  
                  <div className="application-routes">
                    <h4>Routes and Dosing</h4>
                    <div className="route-card">
                      <div className="route-icon im-icon"></div>
                      <div className="route-details">
                        <h5>Intramuscular (IM)</h5>
                        <p>Testosterone cypionate or enanthate, 50–200 mg weekly or divided twice weekly</p>
                      </div>
                    </div>
                    
                    <div className="route-card">
                      <div className="route-icon transdermal-icon"></div>
                      <div className="route-details">
                        <h5>Transdermal</h5>
                        <p>Gel or cream, 50–100 mg daily</p>
                      </div>
                    </div>
                    
                    <div className="route-card">
                      <div className="route-icon pellet-icon"></div>
                      <div className="route-details">
                        <h5>Pellets</h5>
                        <p>600–1200 mg subcutaneously every 3–6 months</p>
                      </div>
                    </div>
                    
                    <div className="route-card">
                      <div className="route-icon buccal-icon"></div>
                      <div className="route-details">
                        <h5>Buccal/Nasal</h5>
                        <p>Less commonly used; 30 mg buccal twice daily or 11 mg nasal three times daily</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="image-container">
                  <div id="testosterone-metabolism" className="diagram">
                    {/* Diagram will be inserted by JavaScript */}
                  </div>
                  <p className="caption">Figure 2: Testosterone Metabolism and Conversion Pathways</p>
                </div>
              </div>
              
              <div className="subsection">
                <h3>2. Adjunctive Hormones</h3>
                
                <div className="accordion">
                  <div className="accordion-item">
                    <div className="accordion-header">
                      <h4>Progesterone</h4>
                      <span className="accordion-icon">+</span>
                    </div>
                    <div className="accordion-content">
                      <h5>Indications</h5>
                      <p>Sleep disturbances, snoring, or suspected sleep apnea.</p>
                      
                      <h5>Dosing</h5>
                      <p>Oral micronized progesterone, starting at 20–50 mg at night, titrated up to 200 mg nightly.</p>
                      
                      <h5>Mechanism</h5>
                      <p>May reduce IL-16 levels associated with hypoxia, improve oxygenation during sleep.</p>
                    </div>
                  </div>
                  
                  <div className="accordion-item">
                    <div className="accordion-header">
                      <h4>DHEA Supplementation</h4>
                      <span className="accordion-icon">+</span>
                    </div>
                    <div className="accordion-content">
                      <h5>Indications</h5>
                      <p>Low DHEA-S, fatigue, immune dysregulation.</p>
                      
                      <h5>Dosing</h5>
                      <p>Start with 10–25 mg/day, titrate based on clinical response and laboratory values.</p>
                      <p>Dosing may be adjusted according to adrenal stage.</p>
                      
                      <h5>Mechanism</h5>
                      <p>Supports immune modulation, balances cortisol, enhances T-cell function.</p>
                    </div>
                  </div>
                  
                  <div className="accordion-item">
                    <div className="accordion-header">
                      <h4>Pregnenolone Supplementation</h4>
                      <span className="accordion-icon">+</span>
                    </div>
                    <div className="accordion-content">
                      <h5>Indications</h5>
                      <p>Adrenal support, low cortisol and DHEA.</p>
                      
                      <h5>Dosing</h5>
                      <ul>
                        <li>Stage 1 (high cortisol, low DHEA): 5–15 mg three times daily</li>
                        <li>Stage 2 (transitional): 12–30 mg three times daily</li>
                        <li>Stage 3 (low cortisol, low DHEA): up to 50 mg three times daily</li>
                      </ul>
                      
                      <h5>Mechanism</h5>
                      <p>Supports cortisol production, serves as precursor for steroidogenesis.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="subsection">
                <h3>3. Adjunctive Peptide Therapy</h3>
                
                <div className="peptide-grid">
                  <div className="peptide-card">
                    <h4>Growth Hormone Secretagogues</h4>
                    <ul>
                      <li><strong>CJC-1295 + Ipamorelin:</strong> 100 mcg each, subcutaneously at night (initial), may increase to 2–3 times daily for active individuals</li>
                      <li><strong>Tesamorelin + Ipamorelin:</strong> 1 mg + 100 mcg daily (first 4 weeks)</li>
                    </ul>
                    <p><strong>Mechanism:</strong> Enhance endogenous GH pulsatility, improve IGF-1, support mitochondrial function, promote autophagy</p>
                  </div>
                  
                  <div className="peptide-card">
                    <h4>Thymosin Alpha-1 (TA1)</h4>
                    <p><strong>Dosing:</strong> 1.5 mg subcutaneously, once daily, 5 days per week</p>
                    <p><strong>Mechanism:</strong> Immune modulation, supports T-cell function, enhances NADPH and redox balance</p>
                  </div>
                  
                  <div className="peptide-card">
                    <h4>BPC-157</h4>
                    <p><strong>Dosing:</strong> 500 mcg daily, oral or subcutaneous</p>
                    <p><strong>Mechanism:</strong> Supports gut healing, reduces inflammation, aids tissue repair</p>
                  </div>
                  
                  <div className="peptide-card">
                    <h4>Alpha-GPC</h4>
                    <p><strong>Dosing:</strong> 600 mg, 90 minutes prior to exercise</p>
                    <p><strong>Mechanism:</strong> Enhances cognitive function and GH release</p>
                  </div>
                </div>
              </div>
              
              <div className="subsection">
                <h3>4. Supplementary Protocols</h3>
                
                <div className="grid-2-col">
                  <div className="protocol-card">
                    <h4>Adaptogenic Herbs and Adrenal Support</h4>
                    <ul>
                      <li>Ashwagandha: 300–600 mg twice daily</li>
                      <li>Rhodiola rosea: 200–400 mg daily</li>
                      <li>Holy basil: 300–600 mg daily</li>
                      <li>Licorice root extract (Stage 3 only): 200–400 mg with breakfast and lunch</li>
                    </ul>
                    <p><strong>Mechanism:</strong> Modulate cortisol, improve stress resilience, prolong cortisol half-life (licorice)</p>
                  </div>
                  
                  <div className="protocol-card">
                    <h4>Blood Sugar Support</h4>
                    <ul>
                      <li>Alpha-lipoic acid: 300–600 mg daily</li>
                      <li>Chromium: 200–400 mcg daily</li>
                      <li>Berberine: 500 mg three times daily</li>
                      <li>Magnesium: 300–400 mg daily</li>
                    </ul>
                    <p><strong>Mechanism:</strong> Stabilize blood sugar, reduce unnecessary cortisol production</p>
                  </div>
                </div>
              </div>
              
              <div className="subsection">
                <h3>5. Estrogen Management</h3>
                
                <div className="grid-2-col">
                  <div>
                    <h4>Aromatase Inhibitors (if needed)</h4>
                    <ul>
                      <li>Calcium D-glucarate: 500–1000 mg twice daily</li>
                      <li>DIM: 100–200 mg daily</li>
                      <li>Chrysin: 500–1000 mg daily</li>
                      <li>Zinc: 30–50 mg daily</li>
                      <li>Anastrozole: 0.5–1 mg 2–3 times weekly (if pharmacologic intervention required)</li>
                      <li>Letrozole: 2.5 mg every other week</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4>Indications</h4>
                    <p>Estradiol:testosterone ratio {'>'}5%, or {'>'}3% with symptoms (gynecomastia, mood changes).</p>
                    
                    <h4>Goal</h4>
                    <p>Maintain estradiol:testosterone ratio between 3–5%.</p>
                    
                    <div className="image-container">
                      <div id="estradiol-ratio" className="chart-container">
                        <canvas id="estradiolChart"></canvas>
                      </div>
                      <p className="caption">Figure 3: Optimal Estradiol:Testosterone Ratio</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="subsection">
                <h3>6. Alternative and Fertility-Preserving Therapies</h3>
                
                <div className="grid-2-col">
                  <div className="fertility-card">
                    <h4>Clomiphene Citrate (Clomid)</h4>
                    <p><strong>Dosing:</strong> 25–50 mg, 5 days per week</p>
                    <p><strong>Mechanism:</strong> Increases LH/FSH, stimulates endogenous testosterone, preserves fertility</p>
                  </div>
                  
                  <div className="fertility-card">
                    <h4>Gonadarelin</h4>
                    <p><strong>Dosing:</strong> 100 mcg subcutaneous twice weekly or 200–300 mcg sublingual three times weekly</p>
                    <p><strong>Mechanism:</strong> Stimulates LH/FSH</p>
                  </div>
                  
                  <div className="fertility-card">
                    <h4>Human Chorionic Gonadotropin (hCG)</h4>
                    <p><strong>Dosing:</strong> 250–1500 IU subcutaneously 2–3 times weekly</p>
                    <p><strong>Mechanism:</strong> Preserves testicular function and size</p>
                  </div>
                  
                  <div className="fertility-card">
                    <h4>Testosterone Booster (Compounded)</h4>
                    <p><strong>Composition:</strong> Combination of Clomid, DHEA, 7-keto-DHEA, progesterone, and anastrozole</p>
                    <p><strong>Effect:</strong> Can increase testosterone by ~100 ng/dL</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="monitoring" className="section">
            <div className="container">
              <h2>III. Monitoring and Adjustments</h2>
              
              <div className="grid-2-col">
                <div className="subsection">
                  <h3>1. Follow-Up Schedule</h3>
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-marker">8-12 wk</div>
                      <div className="timeline-content">
                        <p>Hormone levels, symptom assessment, safety labs (CBC, liver function, PSA)</p>
                      </div>
                    </div>
                    
                    <div className="timeline-item">
                      <div className="timeline-marker">3 mo</div>
                      <div className="timeline-content">
                        <p>Comprehensive hormone panel</p>
                      </div>
                    </div>
                    
                    <div className="timeline-item">
                      <div className="timeline-marker">6 mo</div>
                      <div className="timeline-content">
                        <p>DEXA scan, hormone panel, PSA, hematocrit</p>
                      </div>
                    </div>
                    
                    <div className="timeline-item">
                      <div className="timeline-marker">12 mo</div>
                      <div className="timeline-content">
                        <p>PSA, hematocrit, liver function tests, comprehensive review</p>
                      </div>
                    </div>
                  </div>
                  
                  <h3>3. Dose Adjustments</h3>
                  <ul>
                    <li>Adjust based on laboratory results and symptom response</li>
                    <li>Aim for optimal physiological ranges, not just "normal" ranges</li>
                    <li>Consider subjective experience alongside objective measures</li>
                  </ul>
                </div>
                
                <div className="subsection">
                  <h3>2. Clinical Outcomes</h3>
                  <h4>Expected Improvements</h4>
                  <div className="outcomes-grid">
                    <div className="outcome-item">
                      <div className="outcome-icon energy-icon"></div>
                      <p>Energy</p>
                    </div>
                    
                    <div className="outcome-item">
                      <div className="outcome-icon libido-icon"></div>
                      <p>Libido</p>
                    </div>
                    
                    <div className="outcome-item">
                      <div className="outcome-icon muscle-icon"></div>
                      <p>Muscle Mass</p>
                    </div>
                    
                    <div className="outcome-item">
                      <div className="outcome-icon fat-icon"></div>
                      <p>Visceral Fat Reduction</p>
                    </div>
                    
                    <div className="outcome-item">
                      <div className="outcome-icon sleep-icon"></div>
                      <p>Sleep Quality</p>
                    </div>
                    
                    <div className="outcome-item">
                      <div className="outcome-icon mental-icon"></div>
                      <p>Mental Clarity</p>
                    </div>
                  </div>
                  
                  <h4>Monitor for Adverse Effects</h4>
                  <ul>
                    <li>Polycythemia</li>
                    <li>Gynecomastia</li>
                    <li>Mood changes</li>
                    <li>Acne</li>
                    <li>Hair loss</li>
                    <li>Testicular atrophy</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section id="special-considerations" className="section">
            <div className="container">
              <h2>IV. Special Considerations</h2>
              
              <div className="grid-2-col">
                <div className="special-card">
                  <h3>1. Avoiding Supraphysiologic IGF-1</h3>
                  <p>High IGF-1 can disrupt SIRT1/p53 interaction, leading to premature senescence.</p>
                  <p>Maintain physiologic IGF-1 by using secretagogues rather than exogenous GH.</p>
                </div>
                
                <div className="special-card">
                  <h3>2. Sleep Apnea and Snoring</h3>
                  <p>Oral progesterone (up to 200 mg) may alleviate symptoms by reducing IL-16 and improving oxygenation.</p>
                </div>
                
                <div className="special-card">
                  <h3>3. Chronic Fatigue or Immune Dysregulation</h3>
                  <p>Begin with immune-modulating peptides (TA1, TB4) before introducing GHRH/GHRP to avoid exacerbating inflammation.</p>
                </div>
                
                <div className="special-card">
                  <h3>4. Age-Related Considerations</h3>
                  <p>DHEA and testosterone requirements may decrease with age.</p>
                  <p>Older men may require lower doses; monitor cardiovascular risk factors more closely.</p>
                </div>
              </div>
              
              <div className="subsection">
                <h3>5. Contraindications</h3>
                <div className="grid-2-col">
                  <div className="contraindication-card absolute">
                    <h4>Absolute Contraindications</h4>
                    <ul>
                      <li>Active prostate or breast cancer</li>
                      <li>Acute thromboembolic disorders</li>
                      <li>Acute myocardial infarction</li>
                      <li>Severe liver/cardiac/renal disease</li>
                    </ul>
                  </div>
                  
                  <div className="contraindication-card relative">
                    <h4>Relative Contraindications</h4>
                    <ul>
                      <li>History of prostate cancer (urological clearance required)</li>
                      <li>Significant cardiovascular disease</li>
                      <li>Elevated PSA</li>
                      <li>Untreated sleep apnea</li>
                      <li>Polycythemia</li>
                      <li>Desire for fertility</li>
                      <li>Severe psychiatric disorders</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="scientific-rationale" className="section">
            <div className="container">
              <h2>V. Scientific Rationale and Evidence</h2>
              
              <div className="grid-2-col">
                <div className="subsection">
                  <h3>Mechanistic Rationale</h3>
                  <div className="mechanism-grid">
                    <div className="mechanism-card">
                      <h4>GH/IGF-1 Axis</h4>
                      <p>Supports mitochondrial function, autophagy, and cellular repair</p>
                    </div>
                    
                    <div className="mechanism-card">
                      <h4>Testosterone</h4>
                      <p>Improves metabolic health, body composition, bone density, cognitive function, and mood</p>
                    </div>
                    
                    <div className="mechanism-card">
                      <h4>Progesterone</h4>
                      <p>Modulates IL-16, supports sleep and respiratory function</p>
                    </div>
                    
                    <div className="mechanism-card">
                      <h4>DHEA</h4>
                      <p>Immune modulator, balances cortisol, supports T-cell activation</p>
                    </div>
                    
                    <div className="mechanism-card">
                      <h4>TA1/TB4</h4>
                      <p>Immune rebalancing, enhances NADPH and redox control</p>
                    </div>
                    
                    <div className="mechanism-card">
                      <h4>BPC-157</h4>
                      <p>Tissue repair, gut-brain axis support</p>
                    </div>
                  </div>
                </div>
                
                <div className="subsection">
                  <h3>Evidence-Based Effects</h3>
                  <div className="evidence-table">
                    <div className="table-row header">
                      <div className="cell">Effect Domain</div>
                      <div className="cell">Evidence Summary</div>
                    </div>
                    
                    <div className="table-row">
                      <div className="cell">Metabolic</div>
                      <div className="cell">Improved insulin sensitivity, body composition, lipid profile</div>
                    </div>
                    
                    <div className="table-row">
                      <div className="cell">Cardiovascular</div>
                      <div className="cell">Potential cardioprotective effects; low testosterone associated with increased CVD risk</div>
                    </div>
                    
                    <div className="table-row">
                      <div className="cell">Prostate Health</div>
                      <div className="cell">No evidence that TRT increases prostate cancer risk; PSA monitoring remains essential</div>
                    </div>
                    
                    <div className="table-row">
                      <div className="cell">Bone Health</div>
                      <div className="cell">Increases bone mineral density, reduces fracture risk</div>
                    </div>
                    
                    <div className="table-row">
                      <div className="cell">Cognitive Function</div>
                      <div className="cell">May improve executive function and mood</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="subsection">
                <h3>Lifestyle Modifications</h3>
                <div className="grid-4-col">
                  <div className="lifestyle-card">
                    <div className="lifestyle-icon diet-icon"></div>
                    <h4>Diet</h4>
                    <ul>
                      <li>Whole, unprocessed foods</li>
                      <li>Adequate protein</li>
                      <li>Healthy fats</li>
                      <li>Minimize refined carbs</li>
                      <li>Intermittent fasting if appropriate</li>
                      <li>Limit alcohol</li>
                    </ul>
                  </div>
                  
                  <div className="lifestyle-card">
                    <div className="lifestyle-icon exercise-icon"></div>
                    <h4>Exercise</h4>
                    <ul>
                      <li>Resistance training (3–4x/week)</li>
                      <li>HIIT (1–2x/week)</li>
                      <li>Moderate aerobic (2–3x/week)</li>
                      <li>Avoid overtraining</li>
                    </ul>
                  </div>
                  
                  <div className="lifestyle-card">
                    <div className="lifestyle-icon stress-icon"></div>
                    <h4>Stress Management</h4>
                    <ul>
                      <li>Meditation</li>
                      <li>Mindfulness</li>
                      <li>Adequate sleep (7–9 hours)</li>
                      <li>Social support</li>
                    </ul>
                  </div>
                  
                  <div className="lifestyle-card">
                    <div className="lifestyle-icon sleep-icon"></div>
                    <h4>Sleep Optimization</h4>
                    <ul>
                      <li>Consistent schedule</li>
                      <li>Sleep hygiene</li>
                      <li>Blue light elimination at night</li>
                      <li>Cool bedroom temperature</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="dosing-summary" className="section">
            <div className="container">
              <h2>VI. Summary of Dosing Schedule</h2>
              
              <div className="dosing-table">
                <div className="table-row header">
                  <div className="cell">Agent</div>
                  <div className="cell">Dose</div>
                  <div className="cell">Frequency</div>
                  <div className="cell">Route</div>
                </div>
                
                <div className="table-row">
                  <div className="cell">Testosterone</div>
                  <div className="cell">Individualized</div>
                  <div className="cell">Weekly or daily</div>
                  <div className="cell">IM or transdermal</div>
                </div>
                
                <div className="table-row">
                  <div className="cell">Progesterone</div>
                  <div className="cell">20–200 mg</div>
                  <div className="cell">Nightly</div>
                  <div className="cell">Oral</div>
                </div>
                
                <div className="table-row">
                  <div className="cell">DHEA</div>
                  <div className="cell">10–25 mg</div>
                  <div className="cell">Daily</div>
                  <div className="cell">Oral</div>
                </div>
                
                <div className="table-row">
                  <div className="cell">Pregnenolone</div>
                  <div className="cell">5–50 mg</div>
                  <div className="cell">3x daily</div>
                  <div className="cell">Oral</div>
                </div>
                
                <div className="table-row">
                  <div className="cell">CJC-1295 + Ipamorelin</div>
                  <div className="cell">100 mcg each</div>
                  <div className="cell">Nightly (initial)</div>
                  <div className="cell">Subcutaneous</div>
                </div>
                
                <div className="table-row">
                  <div className="cell">Thymosin Alpha-1</div>
                  <div className="cell">1.5 mg</div>
                  <div className="cell">Daily, 5 days/week</div>
                  <div className="cell">Subcutaneous</div>
                </div>
                
                <div className="table-row">
                  <div className="cell">BPC-157</div>
                  <div className="cell">500 mcg</div>
                  <div className="cell">Daily</div>
                  <div className="cell">Oral or subcutaneous</div>
                </div>
                
                <div className="table-row">
                  <div className="cell">hCG (if needed)</div>
                  <div className="cell">250–1500 IU</div>
                  <div className="cell">2–3x weekly</div>
                  <div className="cell">Subcutaneous</div>
                </div>
              </div>
            </div>
          </section>

          <section id="conclusion" className="section">
            <div className="container">
              <h2>Conclusion</h2>
              <p>This comprehensive clinical protocol for hormone replacement therapy in men integrates mechanistic understanding with practical clinical implementation. By addressing not only testosterone replacement but also supporting hormones, peptides, and lifestyle factors, practitioners can optimize patient outcomes while minimizing risks.</p>
              <p>Regular monitoring, personalized dose adjustments, and attention to the specific needs of each patient are essential components of a successful hormone optimization program. When implemented properly, HRT can significantly improve quality of life, metabolic health, and potentially reduce age-related decline in men with documented hypogonadism.</p>
              <div className="citation-note">
                <p><strong>Note:</strong> This protocol synthesizes clinical experience and research evidence but should be adapted to individual patient needs. All interventions should be conducted under appropriate medical supervision with informed consent and consistent monitoring.</p>
              </div>
            </div>
          </section>
        </main>

        <footer>
          <div className="container">
            <p>&copy; 2025 Clinical Education Resource. For educational purposes only. This information is not intended as medical advice.</p>
          </div>
        </footer>
      </div>
      
      {/* Include the scripts component for interactivity */}
      <HRTScripts />
      
    </div>
  );
};

export default SampleMaleHRT;